import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import {
  getConsultation,
  postConsultationMessage,
  patchChecklist,
  activateConsultation,
  declineConsultation,
} from "../lib/architectPortal";
import ArchitectPortalSubnav from "../components/ArchitectPortalSubnav";
import { assetUrl } from "../lib/format";
import { Toaster, toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ChevronLeft,
  CheckCircle2,
  Check,
  XCircle,
  Send,
  ArrowRight,
} from "lucide-react";
import BackBar from "../components/BackBar";
import WorkflowStrip from "../components/WorkflowStrip";
import StatusPill from "../components/StatusPill";

const CHECKLIST = [
  {
    key: "parcel_fit_confirmed",
    label: "Parcel geometric fit confirmed",
    hint: "Width + zoning match the design.",
  },
  {
    key: "site_visit_completed",
    label: "Site visit completed",
    hint: "Walked the lot with the owner.",
  },
  {
    key: "zoning_reviewed",
    label: "Zoning and constraints reviewed",
    hint: "Setbacks, overlays, historic flags considered.",
  },
  {
    key: "iteration_costs_agreed",
    label: "Iteration costs agreed with owner",
    hint: "Variant costs (if any) confirmed in writing.",
  },
  {
    key: "fee_acknowledged",
    label: "Fee acknowledged ($275 Parcel-Fit Consultation)",
    hint: "Owner acknowledged the consultation fee.",
  },
];

export default function ConsultationWorkspace() {
  const { consultId } = useParams();
  const { user } = useRole();
  const navigate = useNavigate();
  const [consult, setConsult] = useState(null);
  const [err, setErr] = useState(null);
  const [composing, setComposing] = useState("");
  const [sending, setSending] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const threadRef = useRef(null);

  async function reload() {
    if (!user?.id) return;
    try {
      const data = await getConsultation(consultId, user.id);
      setConsult(data);
    } catch (e) {
      setErr(e);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultId, user?.id]);

  useEffect(() => {
    // Scroll thread to bottom when messages change
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [consult?.thread?.messages?.length]);

  const checklistDone = useMemo(
    () =>
      CHECKLIST.every(
        (i) => (consult?.activation_checklist || {})[i.key] === true
      ),
    [consult]
  );

  if (err) {
    return (
      <section data-testid="consult-error">
        <ArchitectPortalSubnav />
        <p style={{ color: "#A23B2E" }}>
          Could not load this consultation. It may not be yours.
        </p>
        <button
          className="wn-btn wn-btn-outline mt-4"
          onClick={() => navigate("/architect/inquiries")}
        >
          Back to inbox
        </button>
      </section>
    );
  }

  if (!consult) {
    return (
      <section data-testid="consult-loading">
        <ArchitectPortalSubnav />
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading consultation…
        </div>
      </section>
    );
  }

  async function sendMessage() {
    const body = composing.trim();
    if (!body) return;
    setSending(true);
    try {
      const r = await postConsultationMessage(consultId, user.id, body);
      setConsult((c) => ({
        ...c,
        status: r.consultation.status,
        thread: r.thread,
      }));
      setComposing("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  }

  async function toggleChecklist(key, value) {
    try {
      const r = await patchChecklist(consultId, user.id, key, value);
      setConsult((c) => ({
        ...c,
        activation_checklist: r.activation_checklist,
        checklist_notes: r.checklist_notes,
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not update checklist.";
      toast.error(detail);
    }
  }

  async function activate() {
    try {
      const r = await activateConsultation(consultId, user.id);
      setConsult((c) => ({
        ...c,
        status: r.consultation.status,
        activated_at: r.consultation.activated_at,
        project_id: r.consultation.project_id,
        project: r.project,
      }));
      setShowActivate(false);
      toast.success("Activated. Project created.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not activate.";
      toast.error(detail);
    }
  }

  async function decline(reason) {
    try {
      const r = await declineConsultation(consultId, user.id, reason);
      setConsult((c) => ({
        ...c,
        status: r.status,
        decline_reason: r.decline_reason,
        declined_at: r.declined_at,
      }));
      setShowDecline(false);
      toast.success("Declined.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not decline.";
      toast.error(detail);
    }
  }

  const isOpen = ["Consultation Requested", "In Consultation"].includes(
    consult.status
  );

  return (
    <section
      className="animate-fade-in-up"
      data-testid="consult-workspace"
    >
      <Toaster position="top-right" richColors closeButton />
      <ArchitectPortalSubnav />

      <BackBar
        parent={{ to: "/architect/inquiries", label: "Inquiries" }}
        trail={[
          { label: "Architect", to: "/architect" },
          { label: "Inquiries", to: "/architect/inquiries" },
          { label: consult.owner?.name || consult.parcel?.address || "Consultation" },
        ]}
      />

      {/* Header band */}
      <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1
            style={{
              fontSize: "clamp(24px, 3.4vw, 30px)",
              fontWeight: 600,
              lineHeight: 1.1,
            }}
            data-testid="consult-parcel-address"
          >
            {consult.parcel?.address || "—"}
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 17,
              marginTop: 2,
            }}
          >
            {consult.design?.name || "—"}
            {consult.owner?.name && ` · ${consult.owner.name}`}
          </div>
        </div>
        <StatusPill
          value={consult.status}
          size="md"
          testId={`consult-state-${consult.status.replace(/\s+/g, "_")}`}
        />
      </div>

      {/* Workflow strip — same registry-driven stepper as the Owner Project page. */}
      <WorkflowStrip
        domain="consultation"
        current={consult.status}
        testId="consult-workflow-strip"
      />

      <div
        className="mt-6 grid gap-7"
        style={{ gridTemplateColumns: "minmax(0,1.6fr) minmax(0,1fr)" }}
      >
        {/* LEFT — Thread */}
        <div data-testid="consult-thread-area">
          <div
            ref={threadRef}
            className="wn-card"
            style={{
              padding: "18px 18px",
              minHeight: 320,
              maxHeight: "60vh",
              overflowY: "auto",
            }}
            data-testid="consult-thread"
          >
            {(consult.thread?.messages || []).map((m, i) => (
              <MessageBubble
                key={i}
                msg={m}
                isOutbound={isOutbound(m, consult, user)}
                consult={consult}
              />
            ))}
            {(!consult.thread || consult.thread.messages.length === 0) && (
              <div
                style={{ color: "#6C6B85", fontStyle: "italic", fontSize: 14 }}
              >
                No messages yet.
              </div>
            )}
          </div>

          {isOpen ? (
            <div className="mt-4" data-testid="consult-composer">
              <textarea
                className="wn-input"
                style={{
                  width: "100%",
                  minHeight: 90,
                  padding: 12,
                  lineHeight: 1.5,
                }}
                value={composing}
                onChange={(e) => setComposing(e.target.value)}
                placeholder={`Reply to ${
                  consult.owner?.name?.split(" ")[0] || "the owner"
                }…`}
                data-testid="consult-composer-input"
              />
              <div className="mt-2 flex items-center justify-end gap-2">
                <button
                  className="wn-btn wn-btn-primary"
                  onClick={sendMessage}
                  disabled={!composing.trim() || sending}
                  data-testid="consult-send-btn"
                >
                  <Send size={14} />
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          ) : (
            <div
              className="mt-4 wn-pill wn-pill-info"
              style={{ display: "inline-flex" }}
              data-testid="consult-thread-closed"
            >
              {consult.status === "Activated"
                ? "Conversation continues on the project."
                : "This conversation is closed."}
            </div>
          )}
        </div>

        {/* RIGHT — Checklist + summary */}
        <div className="space-y-5" data-testid="consult-right-rail">
          {consult.status === "Activated" ? (
            <ActivatedPanel consult={consult} />
          ) : consult.status === "Declined" ? (
            <DeclinedPanel consult={consult} />
          ) : (
            <ActivationChecklistPanel
              consult={consult}
              onToggle={toggleChecklist}
              onActivate={() => setShowActivate(true)}
              onDecline={() => setShowDecline(true)}
              allDone={checklistDone}
            />
          )}

          <ContextSummary consult={consult} />
        </div>
      </div>

      <DeclineDialog
        open={showDecline}
        onClose={() => setShowDecline(false)}
        onConfirm={decline}
      />
      <ActivateDialog
        open={showActivate}
        onClose={() => setShowActivate(false)}
        onConfirm={activate}
        consult={consult}
      />
    </section>
  );
}

function isOutbound(msg, consult, user) {
  // architect-id matching to user.id (or architect_id if applicant-approved)
  const archId = user?.architect_id || user?.id;
  return msg.sender_id === archId;
}

// ---------------------------------------------------------------------------
function MessageBubble({ msg, isOutbound, consult }) {
  const ts = fmtTime(msg.sent_at);
  const sender = isOutbound
    ? "You"
    : consult.owner?.name || consult.owner?.organization || "Owner";
  const senderColor = isOutbound ? "#8F3C23" : "#5B6196";
  const justify = isOutbound ? "flex-end" : "flex-start";
  const bubbleStyle = isOutbound
    ? {
        background: "#1B1F4B",
        color: "#FBFAFD",
        border: "1px solid #1B1F4B",
      }
    : {
        background: "#FBFAFD",
        color: "#272636",
        border: "1px solid #D9D8E4",
      };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: justify,
        marginBottom: 14,
      }}
    >
      <div style={{ maxWidth: "78%", minWidth: 220 }}>
        <div
          style={{
            display: "flex",
            justifyContent: justify,
            alignItems: "baseline",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <span
            style={{ fontSize: 12, fontWeight: 700, color: senderColor }}
          >
            {sender}
          </span>
          <span
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 11,
            }}
          >
            {ts}
          </span>
        </div>
        <div
          style={{
            ...bubbleStyle,
            padding: "10px 14px",
            borderRadius: 12,
            fontSize: 14,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
          }}
        >
          {msg.body}
        </div>
      </div>
    </div>
  );
}

function ActivationChecklistPanel({
  consult,
  onToggle,
  onActivate,
  onDecline,
  allDone,
}) {
  const checklist = consult.activation_checklist || {};
  return (
    <div
      className="wn-card"
      style={{ padding: "18px 20px" }}
      data-testid="activation-checklist"
    >
      <h3
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 18,
          color: "#1B1F4B",
        }}
      >
        Activation Checklist
      </h3>
      <p
        className="mt-1"
        style={{ color: "#6C6B85", fontSize: 12, lineHeight: 1.5 }}
      >
        Confirm these before activating the consultation.
      </p>
      <div className="mt-3 space-y-1">
        {CHECKLIST.map((it) => {
          const on = checklist[it.key] === true;
          return (
            <button
              key={it.key}
              type="button"
              onClick={() => onToggle(it.key, !on)}
              data-testid={`checklist-${it.key}`}
              style={{
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: 0,
                borderTop: "1px solid #D9D8E4",
                padding: "10px 0",
                display: "grid",
                gridTemplateColumns: "22px 1fr",
                gap: 10,
                cursor: "pointer",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  border: `1.5px solid ${on ? "#1B1F4B" : "#9AA0C0"}`,
                  background: on ? "#1B1F4B" : "transparent",
                  color: "#FBFAFD",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 2,
                }}
              >
                {on && <Check size={12} />}
              </span>
              <span>
                <span
                  style={{
                    fontWeight: 600,
                    color: "#272636",
                    fontSize: 13,
                  }}
                >
                  {it.label}
                </span>
                <span
                  style={{
                    display: "block",
                    color: "#6C6B85",
                    fontSize: 11,
                    marginTop: 1,
                  }}
                >
                  {it.hint}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <button
        className="wn-btn wn-btn-accent mt-4 w-full"
        onClick={onActivate}
        disabled={!allDone}
        data-testid="activate-btn"
      >
        Activate Consultation <ArrowRight size={14} />
      </button>
      <button
        className="wn-btn wn-btn-tertiary mt-1 w-full"
        onClick={onDecline}
        data-testid="decline-btn"
      >
        Decline inquiry
      </button>
    </div>
  );
}

function ActivatedPanel({ consult }) {
  return (
    <div
      className="wn-card"
      style={{
        padding: "18px 20px",
        borderLeft: "3px solid #4F6149",
        background: "rgba(79,97,73,0.05)",
      }}
      data-testid="activated-panel"
    >
      <div className="flex items-center gap-2">
        <CheckCircle2 size={16} style={{ color: "#4F6149" }} />
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 17,
            color: "#1B1F4B",
            margin: 0,
          }}
        >
          Project created.
        </h3>
      </div>
      <p
        className="mt-2"
        style={{ color: "#272636", fontSize: 13, lineHeight: 1.55 }}
      >
        This consultation was activated{" "}
        {consult.activated_at ? fmtDate(consult.activated_at) : ""}. A new
        project was created and is now in <strong>Selection</strong>.
      </p>
      {consult.project_id && (
        <Link
          to={`/architect/projects`}
          className="wn-btn wn-btn-outline mt-3"
          data-testid="open-project-link"
        >
          Open Projects
        </Link>
      )}
    </div>
  );
}

function DeclinedPanel({ consult }) {
  return (
    <div
      className="wn-card"
      style={{
        padding: "18px 20px",
        borderLeft: "3px solid #A23B2E",
      }}
      data-testid="declined-panel"
    >
      <div className="flex items-center gap-2">
        <XCircle size={16} style={{ color: "#A23B2E" }} />
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 17,
            color: "#1B1F4B",
            margin: 0,
          }}
        >
          Inquiry declined.
        </h3>
      </div>
      <p
        className="mt-2"
        style={{ color: "#272636", fontSize: 13, lineHeight: 1.55 }}
      >
        {consult.decline_reason || "—"}
      </p>
    </div>
  );
}

function ContextSummary({ consult }) {
  const photo = consult.design?.photo ? assetUrl(consult.design.photo) : null;
  const fits = consult.fit?.fits;
  return (
    <div
      className="wn-card"
      style={{ padding: "18px 20px" }}
      data-testid="context-summary"
    >
      {photo && (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            borderRadius: 8,
            backgroundImage: `url(${photo})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            border: "1px solid #D9D8E4",
            marginBottom: 12,
          }}
        />
      )}
      <div
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 17,
          color: "#1B1F4B",
        }}
      >
        {consult.design?.name || "—"}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "#6C6B85",
          marginTop: 2,
          fontStyle: "italic",
          fontFamily: '"Newsreader", Georgia, serif',
        }}
      >
        {consult.parcel?.address || "—"}
      </div>
      <dl
        className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2"
        style={{ fontSize: 12 }}
      >
        <KV label="Lot dimensions">
          {consult.parcel?.width_ft != null
            ? `${consult.parcel.width_ft}′ × ${consult.parcel.depth_ft || "?"}′`
            : "—"}
        </KV>
        <KV label="Zoning">{consult.parcel?.zoning || "—"}</KV>
        <KV label="Min lot width">
          {consult.design?.min_lot_width
            ? `${consult.design.min_lot_width}′`
            : "—"}
        </KV>
        <KV label="Typology">{consult.design?.typology || "—"}</KV>
      </dl>
      <div
        className="mt-3"
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          background: fits
            ? "rgba(79,97,73,0.10)"
            : "rgba(185,138,60,0.12)",
          color: fits ? "#4F6149" : "#8a651f",
          fontSize: 12,
          fontWeight: 600,
        }}
        data-testid="fit-banner"
      >
        {fits ? "✓ " : "! "}
        {fits
          ? "Compatible with this parcel."
          : "Not a standard fit — confirm constraints."}
      </div>
      {consult.design?.id && (
        <Link
          to={`/marketplace/${consult.design.id}`}
          className="wn-btn wn-btn-tertiary mt-3"
          style={{ paddingLeft: 0 }}
        >
          Open design
        </Link>
      )}
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div>
      <dt className="wn-label">{label}</dt>
      <dd
        className="tabular-nums"
        style={{
          marginTop: 1,
          color: "#272636",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {children}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
function DeclineDialog({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const tooShort = reason.trim().length < 10;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setReason("");
          onClose();
        }
      }}
    >
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 520,
        }}
        data-testid="decline-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Decline this inquiry?
          </DialogTitle>
          <DialogDescription
            style={{ color: "#6C6B85", fontSize: 13, lineHeight: 1.5 }}
          >
            The owner will be notified with your reason. They can submit a new
            request later if their circumstances change.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <label className="wn-label">Reason (required, ≥ 10 characters)</label>
          <textarea
            className="wn-input mt-1"
            style={{ minHeight: 110, padding: 10 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="A short, kind explanation. This will be shown to the owner."
            data-testid="decline-reason"
          />
          <div
            className="mt-1 tabular-nums"
            style={{ fontSize: 11, color: "#6C6B85" }}
          >
            {reason.trim().length} / 10
          </div>
        </div>
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={() => {
              setReason("");
              onClose();
            }}
            data-testid="decline-cancel-btn"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-outline"
            style={{ color: "#A23B2E", borderColor: "#1B1F4B" }}
            onClick={async () => {
              if (tooShort) return;
              setBusy(true);
              await onConfirm(reason);
              setBusy(false);
            }}
            disabled={tooShort || busy}
            data-testid="decline-confirm-btn"
          >
            {busy ? "Declining…" : "Confirm decline"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivateDialog({ open, onClose, onConfirm, consult }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 520,
        }}
        data-testid="activate-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Activate this consultation?
          </DialogTitle>
          <DialogDescription
            style={{ color: "#6C6B85", fontSize: 13, lineHeight: 1.5 }}
          >
            This creates a new project in the <strong>Selection</strong> stage
            and notifies {consult.owner?.name || "the owner"}.
          </DialogDescription>
        </DialogHeader>
        <ul
          className="mt-2"
          style={{ fontSize: 13, color: "#272636", paddingLeft: 18 }}
        >
          <li>Project record created with parcel + design + owner linked.</li>
          <li>Consultation status → <strong>Activated</strong>.</li>
          <li>Owner notified by in-app + simulated email.</li>
        </ul>
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={onClose}
            data-testid="activate-cancel-btn"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-accent"
            onClick={async () => {
              setBusy(true);
              await onConfirm();
              setBusy(false);
            }}
            disabled={busy}
            data-testid="activate-confirm-btn"
          >
            {busy ? "Activating…" : "Confirm activation"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
